import * as React from 'react';
import { createPortal } from 'react-dom';
import { Stage, Layer, Group, Rect, Text } from 'react-konva';
import Konva from 'konva';
import BarChart from '../Charts/BarChart';
import LineChart from '../Charts/LineChart';
import PieChart from '../Charts/PieChart';

// Suppress "Several Konva instances" warning — expected when rendering multiple
// Stage components (main canvas + slide thumbnails) on the same page.
Konva.showWarnings = false;

// Default slide size (fits typical backend bbox extent)
export const SLIDE_WIDTH = 1280;
export const SLIDE_HEIGHT = 800;

const HEADER_FILL = '#1e3a5f';
const HEADER_TEXT_FILL = '#fff';
const CELL_FILL = '#ffffff';
const CELL_ALT_FILL = '#f8fafc';
const CELL_STROKE = '#e2e8f0';
const TEXT_FILL = '#1e293b';
const TEXT_BLOCK_FILL = '#f8fafc';
const TEXT_BLOCK_STROKE = '#e2e8f0';
const FONT_SIZE = 14;
const PADDING = 10;
const TITLE_FONT_SIZE = 30;

/** Format a numeric string to a fixed number of decimal places, preserving non-numeric strings. */
function applyDecimalFormat(str, decimalPlaces) {
  if (decimalPlaces == null) return str;
  // Only format if the string looks like a plain number (optional sign, digits, optional dot+digits)
  const trimmed = str.trim();
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
    return parseFloat(trimmed).toFixed(decimalPlaces);
  }
  return str;
}

/** If variable is "Yes", display as %text%; otherwise display as-is (strip surrounding %) and apply decimal formatting. */
function formatDisplayValue(value, variable, decimalPlaces = 2) {
  const str = value != null ? String(value) : '';
  const isVariable = variable === 'Yes' || variable === true;
  if (isVariable) {
    const inner = str.replace(/^%|%$/g, '');
    return inner ? `%${inner}%` : str;
  }
  const stripped = str.replace(/^%|%$/g, '');
  return applyDecimalFormat(stripped, decimalPlaces);
}

/** Normalize element bbox: supports both [x,y,w,h] array and {x,y,width,height} object formats. */
function getBbox(element) {
  if (Array.isArray(element.bbox) && element.bbox.length >= 4) {
    return element.bbox.map((v) => Number(v) || 0);
  }
  return [
    Number(element.x) || 0,
    Number(element.y) || 0,
    Number(element.width) || 0,
    Number(element.height) || 0,
  ];
}

const SELECTION_STROKE = '#3b82f6';
const SELECTION_STROKE_WIDTH = 2;

function EditableCell({ children, elementIndex, onStartEdit, path, value, onSelect }) {
  const handleDblClick = (e) => {
    e.cancelBubble = true;
    const node = e.target;
    const stage = node.getStage();
    if (!stage) return;
    const rect = node.getClientRect({ relativeTo: stage });
    const container = stage.container();
    const containerRect = container.getBoundingClientRect();
    const scaleX = containerRect.width / stage.width();
    const scaleY = containerRect.height / stage.height();
    const screenRect = {
      x: containerRect.left + rect.x * scaleX,
      y: containerRect.top + rect.y * scaleY,
      width: Math.max(rect.width * scaleX, 40),
      height: Math.max(rect.height * scaleY, 24),
    };
    onStartEdit(elementIndex, path, value, screenRect);
  };
  const handleClick = (e) => {
    e.cancelBubble = true;
    if (onSelect) onSelect();
  };
  return (
    <Group onDblClick={handleDblClick} onClick={handleClick} listening>
      {children}
    </Group>
  );
}

/** Wrapper to make an element selectable: click to select, show border when selected. */
function SelectableWidget({ elementIndex, bbox, selected, onSelect, children }) {
  const [x, y, w, h] = bbox || [0, 0, 0, 0];
  const handleClick = (e) => {
    e.cancelBubble = true;
    if (onSelect) onSelect(elementIndex);
  };
  return (
    <Group onClick={handleClick} onTap={handleClick} listening>
      {children}
      {selected && (
        <Rect
          x={x - 2}
          y={y - 2}
          width={w + 4}
          height={h + 4}
          stroke={SELECTION_STROKE}
          strokeWidth={SELECTION_STROKE_WIDTH}
          listening={false}
        />
      )}
    </Group>
  );
}

/**
 * Render a table element from backend data.
 * bbox: [x, y, width, height]
 * columns: [{ header, values: [{ value }] }]
 */
function TableElement({ element, scale = 1, elementIndex, editable, onStartEdit, selected, onSelect, decimalPlaces = 2 }) {
  const { columns = [] } = element;
  const [x, y, w, h] = getBbox(element);
  if (!w || !h || columns.length === 0) return null;

  const handleSelect = () => onSelect?.(elementIndex);
  const colCount = columns.length;
  const rowCount = Math.max(1, ...columns.map((c) => (c.values || []).length)) + 1;
  const cellW = w / colCount;
  const cellH = h / rowCount;

  const cells = [];
  columns.forEach((col, cIdx) => {
    const headerContent = (
      <>
        <Rect
          x={x + cIdx * cellW}
          y={y}
          width={cellW}
          height={cellH}
          fill={HEADER_FILL}
          stroke={CELL_STROKE}
          strokeWidth={1}
        />
        <Text
          x={x + cIdx * cellW + PADDING}
          y={y + PADDING}
          width={cellW - PADDING * 2}
          height={cellH - PADDING * 2}
          text={formatDisplayValue(col.header ?? '', col.variable, decimalPlaces)}
          fontSize={FONT_SIZE}
          fontFamily="'Segoe UI', Arial, sans-serif"
          fontStyle="bold"
          fill={HEADER_TEXT_FILL}
          wrap="word"
          ellipsis
          listening={false}
        />
      </>
    );
    cells.push(
      <Group key={`h-${cIdx}`}>
        {editable && onStartEdit ? (
          <EditableCell
            elementIndex={elementIndex}
            onStartEdit={onStartEdit}
            path={['columns', cIdx, 'header']}
            value={col.header || ''}
            onSelect={handleSelect}
          >
            {headerContent}
          </EditableCell>
        ) : (
          headerContent
        )}
      </Group>
    );
  });
  const maxRows = Math.max(...columns.map((c) => (c.values || []).length), 0);
  for (let r = 0; r < maxRows; r++) {
    columns.forEach((col, cIdx) => {
      const val = (col.values || [])[r];
      const rawValue = val && typeof val === 'object' ? val.value : val;
      const variable = val && typeof val === 'object' ? val.variable : col.variable;
      const str = formatDisplayValue(rawValue, variable, decimalPlaces);
      const cellContent = (
        <>
          <Rect
            x={x + cIdx * cellW}
            y={y + (r + 1) * cellH}
            width={cellW}
            height={cellH}
            fill={r % 2 === 0 ? CELL_FILL : CELL_ALT_FILL}
            stroke={CELL_STROKE}
            strokeWidth={1}
          />
          <Text
            x={x + cIdx * cellW + PADDING}
            y={y + (r + 1) * cellH + PADDING}
            width={cellW - PADDING * 2}
            height={cellH - PADDING * 2}
            text={str}
            fontSize={FONT_SIZE - 1}
            fontFamily="'Segoe UI', Arial, sans-serif"
            fill={TEXT_FILL}
            wrap="word"
            ellipsis
            listening={false}
          />
        </>
      );
      cells.push(
        <Group key={`${r}-${cIdx}`}>
          {editable && onStartEdit ? (
            <EditableCell
              elementIndex={elementIndex}
              onStartEdit={onStartEdit}
              path={['columns', cIdx, 'values', r, 'value']}
              value={rawValue != null ? String(rawValue) : ''}
              onSelect={handleSelect}
            >
              {cellContent}
            </EditableCell>
          ) : (
            cellContent
          )}
        </Group>
      );
    });
  }

  return (
    <Group onClick={(e) => { e.cancelBubble = true; onSelect?.(elementIndex); }}>
      {selected && (
        <Rect
          x={x - SELECTION_STROKE_WIDTH / 2}
          y={y - SELECTION_STROKE_WIDTH / 2}
          width={w + SELECTION_STROKE_WIDTH}
          height={h + SELECTION_STROKE_WIDTH}
          stroke={SELECTION_STROKE}
          strokeWidth={SELECTION_STROKE_WIDTH}
          listening={false}
        />
      )}
      <Rect x={x} y={y} width={w} height={h} fill="transparent" listening={false} />
      {cells}
    </Group>
  );
}

/**
 * Render a title element from backend data.
 * bbox: [x, y, width, height], text: string, variable: "Yes" | "No"
 */
function TitleElement({ element, elementIndex, editable, onStartEdit, selected, onSelect, decimalPlaces = 2 }) {
  const { text: rawText, variable } = element;
  const bbox = getBbox(element);
  const [x, y, w, h] = bbox;
  if (!rawText && !editable) return null;

  const textDisplay = formatDisplayValue(rawText, variable, decimalPlaces);

  const content = (
    <Text
      x={x}
      y={y}
      width={w}
      height={h}
      text={textDisplay}
      fontSize={TITLE_FONT_SIZE}
      fontFamily="'Segoe UI', Arial, sans-serif"
      fill={HEADER_FILL}
      fontStyle="bold"
      wrap="word"
      listening={false}
    />
  );

  const inner = editable && onStartEdit ? (
    <EditableCell
      elementIndex={elementIndex}
      onStartEdit={onStartEdit}
      path={['text']}
      value={rawText || ''}
      onSelect={() => onSelect?.(elementIndex)}
    >
      {content}
    </EditableCell>
  ) : (
    content
  );

  return (
    <SelectableWidget elementIndex={elementIndex} bbox={bbox} selected={selected} onSelect={onSelect}>
      {inner}
    </SelectableWidget>
  );
}

/**
 * Render a text element from backend data.
 * bbox: [x, y, width, height], text: string
 */
function TextElement({ element, elementIndex, editable, onStartEdit, selected, onSelect, decimalPlaces = 2 }) {
  const { text: rawText, variable } = element;
  const bbox = getBbox(element);
  const [x, y, w, h] = bbox;
  if (!rawText && !editable) return null;

  const text = formatDisplayValue(rawText, variable, decimalPlaces);
  const lines = text.split('\n');
  const textDisplay = lines.join('\n');

  const content = (
    <>
      <Rect
        x={x}
        y={y}
        width={w}
        height={h}
        fill={TEXT_BLOCK_FILL}
        stroke={TEXT_BLOCK_STROKE}
        strokeWidth={1}
      />
      <Text
        x={x + PADDING}
        y={y + PADDING}
        width={w - PADDING * 2}
        height={h - PADDING * 2}
        text={textDisplay}
        fontSize={FONT_SIZE}
        fontFamily="'Segoe UI', Arial, sans-serif"
        fill={TEXT_FILL}
        wrap="word"
        lineHeight={1.4}
        listening={false}
      />
    </>
  );

  const inner = editable && onStartEdit ? (
    <EditableCell
      elementIndex={elementIndex}
      onStartEdit={onStartEdit}
      path={['text']}
      value={rawText || ''}
      onSelect={() => onSelect?.(elementIndex)}
    >
      {content}
    </EditableCell>
  ) : (
    content
  );

  return (
    <SelectableWidget elementIndex={elementIndex} bbox={bbox} selected={selected} onSelect={onSelect}>
      {inner}
    </SelectableWidget>
  );
}

const CHART_COLORS = ['#3b82f6', '#f59e0b', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4', '#f97316'];

/** Map backend chart_type (e.g. BAR_STACKED, LINE, PIE) to a simple key. */
function getChartVariant(chartType) {
  const t = (chartType || '').toUpperCase();
  if (t.includes('PIE') || t.includes('DOUGHNUT')) return 'pie';
  if (t.includes('LINE')) return 'line';
  return 'bar';
}

/** Build chart data from metadata: categories + series -> [{ label, value }]. */
function buildChartData(element) {
  const { categories = [], series = [] } = element;
  const values = series[0]?.values ?? [];
  if (categories.length === 0 && values.length === 0) return [];
  const n = Math.max(categories.length, values.length);
  return Array.from({ length: n }, (_, i) => ({
    label: categories[i] ?? `Item ${i + 1}`,
    value: Number(values[i]) || 0,
  }));
}

/**
 * Renders a chart from metadata in an absolutely positioned div (overlay).
 * bbox: [x, y, width, height]; chart_type, categories, series from backend.
 */
function ChartOverlay({ element, scaleX, scaleY, elementIndex, selected, onSelect }) {
  const { chart_type } = element;
  const [x, y, w, h] = getBbox(element);
  const data = buildChartData(element);
  const variant = getChartVariant(chart_type);

  if (data.length === 0) return null;

  const style = {
    position: 'absolute',
    left: x * scaleX,
    top: y * scaleY,
    width: w * scaleX,
    height: h * scaleY,
    overflow: 'hidden',
    backgroundColor: '#fff',
    border: selected ? `2px solid ${SELECTION_STROKE}` : `1px solid ${CELL_STROKE}`,
    borderRadius: 6,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: onSelect ? 'pointer' : 'default',
  };

  // Size chart to fit the overlay so preview thumb shows the same content as main canvas
  // (avoid min 100px which cropped the chart in thumbnails)
  const padding = 8;
  const chartWidth = Math.max(24, (w * scaleX) - padding * 2);
  const chartHeight = Math.max(24, (h * scaleY) - padding * 2);

  const handleClick = (e) => {
    e.stopPropagation();
    if (onSelect) onSelect(elementIndex);
  };

  if (variant === 'pie') {
    return (
      <div style={style} onClick={handleClick} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(e); }}>
        <PieChart
          width={chartWidth}
          height={chartHeight}
          color={CHART_COLORS}
          data={data}
        />
      </div>
    );
  }
  if (variant === 'line') {
    return (
      <div style={style} onClick={handleClick} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(e); }}>
        <LineChart
          width={chartWidth}
          height={chartHeight}
          color={CHART_COLORS[0]}
          xRange={[0, Math.max(...data.map((d) => d.value), 1)]}
          data={data}
        />
      </div>
    );
  }
  return (
    <div style={style} onClick={handleClick} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(e); }}>
      <BarChart
        width={chartWidth}
        height={chartHeight}
        color={CHART_COLORS}
        xRange={[0, Math.max(...data.map((d) => d.value), 1)]}
        data={data}
      />
    </div>
  );
}

/**
 * Renders a single slide's elements on a Konva Stage.
 * @param {Object} props
 * @param {Array} props.elements - Array of { type, bbox, ... }
 * @param {number} props.width - Stage width
 * @param {number} props.height - Stage height
 * @param {boolean} props.isPreview - If true, scale down for thumbnail
 * @param {boolean} props.editable - If true, double-click to edit text/headers
 * @param {function} props.onElementEdit - (elementIndex, path, value) => void when edit is committed
 * @param {number|null} props.selectedElementIndex - Index of the selected widget (text/table/chart) or null
 * @param {function} props.onSelectElement - (index|null) => void when user selects or deselects a widget
 */
export default function SlideCanvas({
  elements = [],
  width = SLIDE_WIDTH,
  height = SLIDE_HEIGHT,
  isPreview = false,
  editable = false,
  onElementEdit,
  selectedElementIndex = null,
  onSelectElement,
  decimalPlaces = 2,
}) {
  const layerRef = React.useRef(null);
  const [editing, setEditing] = React.useState(null);
  const inputRef = React.useRef(null);

  const scale = Math.min(width / SLIDE_WIDTH, height / SLIDE_HEIGHT);
  const scaleX = scale;
  const scaleY = scale;

  const handleStartEdit = React.useCallback((elementIndex, path, value, screenRect) => {
    setEditing({ elementIndex, path, value, rect: screenRect });
  }, []);

  React.useEffect(() => {
    if (!editing) return;
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [editing]);

  const handleCommitEdit = React.useCallback(
    (newValue) => {
      if (editing && typeof onElementEdit === 'function') {
        onElementEdit(editing.elementIndex, editing.path, newValue);
      }
      setEditing(null);
    },
    [editing, onElementEdit]
  );

  const handleKeyDown = React.useCallback(
    (e) => {
      if (!editing) return;
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleCommitEdit(e.target.value);
      } else if (e.key === 'Escape') {
        setEditing(null);
        e.target.blur();
      }
    },
    [editing, handleCommitEdit]
  );

  const canEdit = editable && typeof onElementEdit === 'function';
  const canSelect = typeof onSelectElement === 'function' && !isPreview;

  return (
    <>
      <div style={{ position: 'relative', width, height }}>
        <Stage width={width} height={height}>
          <Layer ref={layerRef} scaleX={scaleX} scaleY={scaleY}>
            <Rect
              x={0}
              y={0}
              width={SLIDE_WIDTH}
              height={SLIDE_HEIGHT}
              fill="#fff"
              listening={canSelect}
              onClick={(e) => { e.cancelBubble = true; onSelectElement?.(null); }}
            />
            {elements.map((el, idx) => {
              if (el.type === 'chart') return null;
              const selected = canSelect && selectedElementIndex === idx;
              if (el.type === 'title') {
                return (
                  <TitleElement
                    key={el.shape_id != null ? el.shape_id : idx}
                    element={el}
                    elementIndex={idx}
                    editable={canEdit}
                    onStartEdit={canEdit ? handleStartEdit : undefined}
                    selected={selected}
                    onSelect={canSelect ? onSelectElement : undefined}
                    decimalPlaces={decimalPlaces}
                  />
                );
              }
              if (el.type === 'table') {
                return (
                  <TableElement
                    key={el.shape_id != null ? el.shape_id : idx}
                    element={el}
                    elementIndex={idx}
                    editable={canEdit}
                    onStartEdit={canEdit ? handleStartEdit : undefined}
                    selected={selected}
                    onSelect={canSelect ? onSelectElement : undefined}
                    decimalPlaces={decimalPlaces}
                  />
                );
              }
              if (el.type === 'text' || el.type === 'textbox') {
                return (
                  <TextElement
                    key={el.shape_id != null ? el.shape_id : idx}
                    element={el}
                    elementIndex={idx}
                    editable={canEdit}
                    onStartEdit={canEdit ? handleStartEdit : undefined}
                    selected={selected}
                    onSelect={canSelect ? onSelectElement : undefined}
                    decimalPlaces={decimalPlaces}
                  />
                );
              }
              return null;
            })}
          </Layer>
        </Stage>
        {elements.map((el, idx) =>
          el.type === 'chart' ? (
            <ChartOverlay
              key={el.shape_id != null ? el.shape_id : idx}
              element={el}
              scaleX={scaleX}
              scaleY={scaleY}
              elementIndex={idx}
              selected={canSelect && selectedElementIndex === idx}
              onSelect={canSelect ? onSelectElement : undefined}
            />
          ) : null
        )}
      </div>

      {editing &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              left: editing.rect.x,
              top: editing.rect.y,
              zIndex: 10000,
              padding: 0,
              margin: 0,
              border: `2px solid ${SELECTION_STROKE}`,
              borderRadius: 4,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              overflow: 'hidden',
            }}
          >
            <textarea
              ref={inputRef}
              defaultValue={editing.value}
              onBlur={(e) => handleCommitEdit(e.target.value)}
              onKeyDown={handleKeyDown}
              style={{
                width: editing.rect.width - 4,
                height: editing.rect.height - 4,
                margin: 0,
                padding: 4,
                border: 'none',
                outline: 'none',
                fontSize: 14,
                fontFamily: 'Arial',
                resize: 'none',
                lineHeight: 1.3,
              }}
            />
          </div>,
          document.body
        )}
    </>
  );
}
